import { useState, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateCards, addCard, clearAllCards, setActiveLayout } from '../store/cardsSlice';
import { layoutPresets } from '../config/layoutPresets.tsx';
import type { LayoutOption } from '@ocktra/shared-types';

const Layouts = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const cards = useAppSelector((state) => state.cards.cards);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const applyLayout = (layout: LayoutOption) => {
        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight - 108; // Subtract nav (60px) + layout nav bar (48px)

        // Clear all existing cards first
        dispatch(clearAllCards());

        // Clear active layout since we're applying a preset
        dispatch(setActiveLayout(null));

        // Then apply the layout positions/sizes with percentage-based calculations
        // We need to wait a moment for the cards to be added to the store
        setTimeout(() => {
            const layoutData = layout.cards.map((layoutCard, index) => {
                // Calculate pixel values from percentages
                const responsiveCard = {
                    ...layoutCard,
                    position: {
                        x: (layoutCard.position.x / 100) * viewportWidth,
                        y: (layoutCard.position.y / 100) * viewportHeight
                    },
                    size: {
                        width: (layoutCard.size.width / 100) * viewportWidth,
                        height: (layoutCard.size.height / 100) * viewportHeight
                    }
                };

                console.log("Applying card layout:", responsiveCard);

                // Find the card index by title
                const cardIndex = cards.findIndex(card => card.title === layoutCard.title);

                const newCard = {
                    ...responsiveCard,
                    index: cardIndex >= 0 ? cardIndex : index
                }

                dispatch(addCard(newCard));

                return {
                    ...responsiveCard,
                    index: cardIndex >= 0 ? cardIndex : index
                };
            });

            dispatch(updateCards(layoutData));
        }, 50);

        setIsOpen(false);
    };

    return (
        <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border-light)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: '0.9375rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    transition: 'all 200ms ease-in-out'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                    e.currentTarget.style.borderColor = 'var(--color-gray-300)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                    e.currentTarget.style.borderColor = 'var(--color-border-light)';
                }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="3" width="7" height="7" strokeWidth="2" rx="1" />
                    <rect x="14" y="3" width="7" height="7" strokeWidth="2" rx="1" />
                    <rect x="3" y="14" width="7" height="7" strokeWidth="2" rx="1" />
                    <rect x="14" y="14" width="7" height="7" strokeWidth="2" rx="1" />
                </svg>
                Layouts
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    style={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 200ms ease-in-out'
                    }}
                >
                    <polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        left: 0,
                        minWidth: '280px',
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow-xl)',
                        zIndex: 1000,
                        overflow: 'hidden'
                    }}
                >
                    <div style={{
                        padding: 'var(--spacing-sm)',
                        borderBottom: '1px solid var(--color-border-light)',
                        backgroundColor: 'var(--color-gray-50)'
                    }}>
                        <p style={{
                            margin: 0,
                            fontSize: '0.8125rem',
                            fontWeight: '600',
                            color: 'var(--color-text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Select Layout
                        </p>
                    </div>

                    <div style={{ overflowY: 'auto' }}>
                        {layoutPresets.map((layout) => (
                            <button
                                key={layout.id}
                                onClick={() => applyLayout(layout)}
                                style={{
                                    width: '100%',
                                    padding: 'var(--spacing-md)',
                                    border: 'none',
                                    borderBottom: '1px solid var(--color-border-light)',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 'var(--spacing-md)',
                                    transition: 'background-color 200ms ease-in-out',
                                    textAlign: 'left'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <div style={{
                                    flexShrink: 0,
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 'var(--radius-md)',
                                    backgroundColor: 'var(--color-primary-50)',
                                    color: 'var(--color-primary-600)'
                                }}>
                                    {layout.icon}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '0.9375rem',
                                        fontWeight: '600',
                                        color: 'var(--color-text-primary)',
                                        marginBottom: '2px'
                                    }}>
                                        {layout.name}
                                    </div>
                                    <div style={{
                                        fontSize: '0.8125rem',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        {layout.description}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Layouts;
